// Settings → Audit tab (CTSK-389, Phase 4 of EPIC CTSK-385; CTSK-241
// added the second sub-tab below).
//
// Two independent read surfaces share this tab because they both
// answer "who did what, when":
//   • Resources  — GET /audit, reads `resource_audit` (CTSK-386/387):
//     create/update/soft-delete/undelete/hard-delete/restore/rename
//     across documents/tags/storage containers/tools/nests/custom
//     properties.
//   • Users & orgs (CTSK-241) — GET /admin/audit-log, reads the
//     OLDER, unrelated `audit_log` (singular) table that
//     `apps/api/src/lib/audit.ts`'s `recordAudit` writes on every
//     user / org / group / global-role mutation. Different table,
//     different row shape, same `audit:view` cap.
//
// CTSK-241 shipped as a sub-tab here rather than a second top-level
// Settings tab (the brief's primary suggestion) or a Global Roles
// "Timeline" sub-tab (the brief's fallback suggestion) — a second tab
// named close to "Audit" right next to this one would be confusing,
// and this tab is already the audit-browsing home in the product.
// Judgment call, flagged in the CTSK-241 ship report for review.
//
// File location note: the CTSK-389 brief sketched this as
// `apps/web/src/pages/settings/AuditPanel.tsx`, mirroring a
// `pages/settings/PatsPanel.tsx` reference. That reference doesn't
// exist in this repo — every Settings tab (PatsPanel, ToolsPanel,
// OrganizationsPanel, GlobalRolesPanel, UsersPanel, GroupsPanel,
// BundlesPanel…) lives at `apps/web/src/components/<Name>Panel.tsx`
// and is mounted by `pages/Settings.tsx`. This file follows that real
// convention instead of inventing a `pages/settings/` directory.
//
// URL-backed filters: since `Settings` only forwards `section` +
// `nestSlug` as router props (no per-tab query-param prop-threading
// exists for the other panels either), this panel reads its initial
// filter state from `window.location.search` once on mount (same lazy-
// init pattern `DocumentDetail.tsx` uses for `?debugFindings=1`) and
// keeps the URL in sync via `history.replaceState` as filters change
// (same no-remount-navigation pattern the router's `/nt/`, `/dc/`, `/ct/`
// short-link redirects use). That makes `/settings/audit?resourceType=
// document&eventType=soft_deleted` or `/settings/audit?auditView=users&
// actionPrefix=user` a valid, shareable, reload-safe deep link without
// changing Settings' or the router's prop contracts.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { AuditEventRow, AuditEventType, AuditLogActionCategory, AuditLogRow, AuditResourceType } from '@captain-tasko/shared';
import { apiList, ApiError } from '../lib/api.js';
import { SkeletonRows } from './Skeleton.js';
import { UserTypeahead } from './UserTypeahead.js';

type AuditSubView = 'resources' | 'users';

function readSubViewFromUrl(): AuditSubView {
  if (typeof window === 'undefined') return 'resources';
  const sp = new URLSearchParams(window.location.search);
  return sp.get('auditView') === 'users' ? 'users' : 'resources';
}

export function AuditPanel() {
  const [subView, setSubView] = useState<AuditSubView>(() => readSubViewFromUrl());

  function changeSubView(next: AuditSubView) {
    setSubView(next);
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const sp = new URLSearchParams(window.location.search);
      sp.set('section', 'audit');
      if (next === 'resources') sp.delete('auditView');
      else sp.set('auditView', next);
      const qs = sp.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="tabs" style={{ marginBottom: 0 }}>
        <button
          type="button"
          className={clsx('tab', subView === 'resources' && 'active')}
          onClick={() => changeSubView('resources')}
        >
          Resources
        </button>
        <button
          type="button"
          className={clsx('tab', subView === 'users' && 'active')}
          onClick={() => changeSubView('users')}
        >
          Users &amp; orgs
        </button>
      </div>
      {subView === 'resources' ? <ResourceAuditPanel /> : <UserOrgAuditLogPanel />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Resources sub-tab — GET /audit over `resource_audit` (CTSK-389, unchanged
// by CTSK-241 aside from being extracted into its own component below).
// ─────────────────────────────────────────────────────────────────────────

const RESOURCE_TYPES: AuditResourceType[] = [
  'document', 'tag', 'storage_container', 'tool', 'nest', 'custom_property', 'note', 'file_workflow',
];
const EVENT_TYPES: AuditEventType[] = [
  'created', 'updated', 'soft_deleted', 'undeleted', 'hard_deleted', 'restored', 'renamed',
];

const RESOURCE_TYPE_LABELS: Record<AuditResourceType, string> = {
  document: 'Document',
  tag: 'Tag',
  storage_container: 'Storage container',
  tool: 'Tool',
  nest: 'Nest',
  custom_property: 'Custom property',
  note: 'Note',
  file_workflow: 'File workflow',
};

const EVENT_TYPE_LABELS: Record<AuditEventType, string> = {
  created: 'Created',
  updated: 'Updated',
  soft_deleted: 'Soft-deleted',
  undeleted: 'Undeleted',
  hard_deleted: 'Hard-deleted',
  restored: 'Restored',
  renamed: 'Renamed',
};

interface AuditFilters {
  resourceType: AuditResourceType | '';
  eventType: AuditEventType | '';
  actorUserId: string;
  actorLabel: string; // display-only, not sent to the API
  dateFrom: string; // YYYY-MM-DD
  dateTo: string; // YYYY-MM-DD
}

function readFiltersFromUrl(): AuditFilters {
  if (typeof window === 'undefined') {
    return { resourceType: '', eventType: '', actorUserId: '', actorLabel: '', dateFrom: '', dateTo: '' };
  }
  const sp = new URLSearchParams(window.location.search);
  const rt = sp.get('resourceType');
  const et = sp.get('eventType');
  return {
    resourceType: rt && (RESOURCE_TYPES as string[]).includes(rt) ? (rt as AuditResourceType) : '',
    eventType: et && (EVENT_TYPES as string[]).includes(et) ? (et as AuditEventType) : '',
    actorUserId: sp.get('actorUserId') ?? '',
    actorLabel: sp.get('actorLabel') ?? '',
    dateFrom: sp.get('dateFrom') ?? '',
    dateTo: sp.get('dateTo') ?? '',
  };
}

/** Push the current filters into the URL without a router navigation —
 *  same replaceState pattern router.tsx's short-link redirects use. */
function syncFiltersToUrl(filters: AuditFilters) {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const sp = new URLSearchParams(window.location.search);
  sp.set('section', 'audit');
  const setOrDelete = (k: string, v: string) => {
    if (v) sp.set(k, v);
    else sp.delete(k);
  };
  setOrDelete('resourceType', filters.resourceType);
  setOrDelete('eventType', filters.eventType);
  setOrDelete('actorUserId', filters.actorUserId);
  setOrDelete('actorLabel', filters.actorLabel);
  setOrDelete('dateFrom', filters.dateFrom);
  setOrDelete('dateTo', filters.dateTo);
  const qs = sp.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  window.history.replaceState(null, '', next);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** Actor display: resolved display name when the API could batch-
 *  resolve `actorUserId` -> a user row; falls back to the raw ULID
 *  (shown in a muted monospace style so it reads as "unresolved id",
 *  not a name) rather than silently swallowing the fallback. PAT
 *  session-tag and system events keep their pre-existing rendering. */
function ActorCell({ row }: { row: AuditEventRow }) {
  if (row.actor) {
    return <span title={row.actor.email ?? undefined}>{row.actor.name ?? row.actor.id}</span>;
  }
  if (row.actorUserId) {
    return (
      <span
        title="Could not resolve a display name for this user id"
        style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}
      >
        {row.actorUserId}
      </span>
    );
  }
  if (row.actorSourceTag) return <code style={{ fontSize: 11 }}>{row.actorSourceTag}</code>;
  return <em style={{ color: 'var(--text-muted)' }}>system</em>;
}

/** One-line preview of the row's reason/changes for the table cell —
 *  the full payload only renders in the drill-down. */
function changesPreview(row: AuditEventRow): string {
  // CTSK-447 — payloadStatus takes priority over the reason/changesJson
  // preview: a caller without audit:view_soft_deleted_content (or
  // looking at a purged target) shouldn't see a "before/after change"
  // preview hinting at content they can't open in the drill-down.
  if (row.payloadStatus === 'purged') return 'content permanently redacted (purged)';
  if (row.payloadStatus === 'not_authorized') return 'content redacted — requires additional access';
  if (row.reason) return row.reason;
  if (!row.changesJson) return '—';
  const keys = Object.keys(row.changesJson);
  if (keys.length === 0) return '—';
  if ('before' in row.changesJson || 'after' in row.changesJson) return 'before/after change';
  return keys.join(', ');
}

function ResourceAuditPanel() {
  const [filters, setFilters] = useState<AuditFilters>(() => readFiltersFromUrl());
  const [actorQuery, setActorQuery] = useState('');
  const [cursors, setCursors] = useState<(string | null)[]>([null]); // stack of page-start cursors
  const [pageIndex, setPageIndex] = useState(0);
  const [drillDown, setDrillDown] = useState<AuditEventRow | null>(null);

  useEffect(() => {
    syncFiltersToUrl(filters);
  }, [filters]);

  // Any filter change resets pagination back to page 1.
  function updateFilters(patch: Partial<AuditFilters>) {
    setFilters((f) => ({ ...f, ...patch }));
    setCursors([null]);
    setPageIndex(0);
  }

  const cursor = cursors[pageIndex] ?? null;

  const q = useQuery({
    queryKey: ['audit', filters.resourceType, filters.eventType, filters.actorUserId, filters.dateFrom, filters.dateTo, cursor],
    queryFn: () =>
      apiList<AuditEventRow>('/audit', {
        query: {
          resourceType: filters.resourceType || undefined,
          eventType: filters.eventType || undefined,
          actorUserId: filters.actorUserId || undefined,
          dateFrom: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
          dateTo: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
          cursor: cursor ?? undefined,
          limit: 50,
        },
      }),
  });

  const rows = q.data?.data ?? [];
  const nextCursor = q.data?.page.nextCursor ?? null;

  const hasNextPage = !!nextCursor;
  const hasPrevPage = pageIndex > 0;

  function goNext() {
    if (!nextCursor) return;
    setCursors((c) => {
      const next = c.slice(0, pageIndex + 1);
      next.push(nextCursor);
      return next;
    });
    setPageIndex((i) => i + 1);
  }
  function goPrev() {
    if (pageIndex === 0) return;
    setPageIndex((i) => i - 1);
  }

  const anyFilterActive = useMemo(
    () => !!(filters.resourceType || filters.eventType || filters.actorUserId || filters.dateFrom || filters.dateTo),
    [filters],
  );

  if (q.isError) {
    const err = q.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          Viewing the audit trail requires the <code>audit:view</code> capability.
        </div>
      );
    }
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load audit events.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Audit</h2>
        </div>
        <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          Full history of create / update / soft-delete / undelete / hard-delete / restore / rename
          events across documents, tags, storage containers, tools, nests, and custom properties.
          Append-only — nothing here can be edited or removed from the UI.
        </p>

        {/* ── Filter bar ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 16 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>Resource type</span>
            <select
              value={filters.resourceType}
              onChange={(e) => updateFilters({ resourceType: e.target.value as AuditResourceType | '' })}
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, minWidth: 160 }}
            >
              <option value="">All resource types</option>
              {RESOURCE_TYPES.map((rt) => (
                <option key={rt} value={rt}>{RESOURCE_TYPE_LABELS[rt]}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>Event</span>
            <select
              value={filters.eventType}
              onChange={(e) => updateFilters({ eventType: e.target.value as AuditEventType | '' })}
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, minWidth: 150 }}
            >
              <option value="">All events</option>
              {EVENT_TYPES.map((et) => (
                <option key={et} value={et}>{EVENT_TYPE_LABELS[et]}</option>
              ))}
            </select>
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, minWidth: 220 }}>
            <span style={{ fontWeight: 500 }}>Actor</span>
            {filters.actorUserId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid var(--border-strong)', borderRadius: 4 }}>
                <span style={{ fontSize: 13, flex: 1 }}>{filters.actorLabel || filters.actorUserId}</span>
                <button
                  type="button"
                  onClick={() => { updateFilters({ actorUserId: '', actorLabel: '' }); setActorQuery(''); }}
                  aria-label="Clear actor filter"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  ×
                </button>
              </div>
            ) : (
              <UserTypeahead
                value={actorQuery}
                onChange={setActorQuery}
                onSelect={(u) => {
                  updateFilters({ actorUserId: u.id, actorLabel: u.displayName });
                  setActorQuery('');
                }}
                placeholder="Filter by actor…"
                ariaLabel="Filter audit events by actor"
              />
            )}
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>From</span>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => updateFilters({ dateFrom: e.target.value })}
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>To</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => updateFilters({ dateTo: e.target.value })}
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4 }}
            />
          </label>

          {anyFilterActive && (
            <button
              type="button"
              className="btn"
              onClick={() => updateFilters({ resourceType: '', eventType: '', actorUserId: '', actorLabel: '', dateFrom: '', dateTo: '' })}
              style={{ fontSize: 12, padding: '6px 10px' }}
            >
              Clear filters
            </button>
          )}
        </div>

        {/* ── Table ──────────────────────────────────────────────── */}
        {q.isLoading ? (
          <SkeletonRows rows={8} />
        ) : rows.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
            No audit events match the current filters.
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-subtle)' }}>
                  <th style={{ padding: '6px 8px' }}>Time</th>
                  <th style={{ padding: '6px 8px' }}>Resource</th>
                  <th style={{ padding: '6px 8px' }}>Event</th>
                  <th style={{ padding: '6px 8px' }}>Actor</th>
                  <th style={{ padding: '6px 8px' }}>Reason / changes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setDrillDown(r)}
                    style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                    className="audit-row"
                  >
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                      {formatTimestamp(r.eventAt)}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {RESOURCE_TYPE_LABELS[r.resourceType]}{' '}
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{r.resourceId}</span>
                    </td>
                    <td style={{ padding: '6px 8px' }}>{EVENT_TYPE_LABELS[r.eventType]}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <ActorCell row={r} />
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{changesPreview(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button type="button" className="btn" disabled={!hasPrevPage} onClick={goPrev} style={{ fontSize: 12, padding: '4px 10px' }}>
                ← Newer
              </button>
              <button type="button" className="btn" disabled={!hasNextPage} onClick={goNext} style={{ fontSize: 12, padding: '4px 10px' }}>
                Older →
              </button>
            </div>
          </>
        )}
      </section>

      {/* ── Drill-down ─────────────────────────────────────────────── */}
      {drillDown && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Audit event detail"
          onClick={() => setDrillDown(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface-raised)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)', padding: 20, maxWidth: 640, width: '90%',
              maxHeight: '80vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>
                {RESOURCE_TYPE_LABELS[drillDown.resourceType]} — {EVENT_TYPE_LABELS[drillDown.eventType]}
              </h3>
              <button type="button" onClick={() => setDrillDown(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
            <dl style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', marginBottom: 12 }}>
              <dt>Resource id</dt><dd style={{ fontFamily: 'var(--font-mono)' }}>{drillDown.resourceId}</dd>
              <dt>Time</dt><dd>{formatTimestamp(drillDown.eventAt)}</dd>
              <dt>Actor</dt><dd><ActorCell row={drillDown} /></dd>
              {drillDown.actorPatId && (<><dt>PAT</dt><dd style={{ fontFamily: 'var(--font-mono)' }}>{drillDown.actorPatId}</dd></>)}
              {drillDown.actorSourceTag && (<><dt>Session tag</dt><dd><code>{drillDown.actorSourceTag}</code></dd></>)}
              {drillDown.reason && (<><dt>Reason</dt><dd>{drillDown.reason}</dd></>)}
            </dl>
            {drillDown.payloadStatus === 'purged' ? (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                This resource has been purged. Its content is permanently redacted — purge is
                irreversible and no capability restores it.
              </p>
            ) : drillDown.payloadStatus === 'not_authorized' ? (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                This resource is soft-deleted. Viewing its content history requires the
                &quot;view soft-deleted content&quot; capability.
              </p>
            ) : drillDown.changesJson && 'before' in drillDown.changesJson ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>BEFORE</div>
                  <pre style={{ fontSize: 11, background: 'var(--surface-sunken)', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
                    {JSON.stringify((drillDown.changesJson as { before?: unknown }).before ?? null, null, 2)}
                  </pre>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>AFTER</div>
                  <pre style={{ fontSize: 11, background: 'var(--surface-sunken)', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
                    {JSON.stringify((drillDown.changesJson as { after?: unknown }).after ?? null, null, 2)}
                  </pre>
                </div>
              </div>
            ) : drillDown.changesJson ? (
              <pre style={{ fontSize: 11, background: 'var(--surface-sunken)', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
                {JSON.stringify(drillDown.changesJson, null, 2)}
              </pre>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No change payload recorded for this event.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Users & orgs sub-tab — CTSK-241. GET /admin/audit-log over `audit_log`.
// ─────────────────────────────────────────────────────────────────────────

// CTSK-428 — 'access' (capability_denied events) added. Per
// docs/audit-log.md §4, the category list is duplicated three places
// (shared type, admin-audit-log.ts route, this file) with no single
// source of truth — keep all three in sync when adding a category.
const ACTION_CATEGORIES: AuditLogActionCategory[] = ['user', 'org', 'group', 'global_role', 'access'];

const ACTION_CATEGORY_LABELS: Record<AuditLogActionCategory, string> = {
  user: 'User accounts',
  org: 'Organizations & membership',
  group: 'Groups',
  global_role: 'Global roles',
  access: 'Access denials',
};

type DatePreset = '24h' | '7d' | '30d' | 'custom';

interface AuditLogFilters {
  datePreset: DatePreset;
  dateFrom: string; // YYYY-MM-DD, only meaningful when datePreset === 'custom'
  dateTo: string; // YYYY-MM-DD, only meaningful when datePreset === 'custom'
  actorUserId: string;
  actorLabel: string;
  targetUserId: string;
  targetLabel: string;
  categories: AuditLogActionCategory[];
}

function readLogFiltersFromUrl(): AuditLogFilters {
  const empty: AuditLogFilters = {
    datePreset: '30d', dateFrom: '', dateTo: '',
    actorUserId: '', actorLabel: '', targetUserId: '', targetLabel: '',
    categories: [],
  };
  if (typeof window === 'undefined') return empty;
  const sp = new URLSearchParams(window.location.search);
  const preset = sp.get('logDatePreset');
  const cats = sp.get('actionPrefix');
  return {
    datePreset: preset === '24h' || preset === '7d' || preset === '30d' || preset === 'custom' ? preset : '30d',
    dateFrom: sp.get('logDateFrom') ?? '',
    dateTo: sp.get('logDateTo') ?? '',
    actorUserId: sp.get('logActorUserId') ?? '',
    actorLabel: sp.get('logActorLabel') ?? '',
    targetUserId: sp.get('logTargetUserId') ?? '',
    targetLabel: sp.get('logTargetLabel') ?? '',
    categories: cats ? cats.split(',').filter((c): c is AuditLogActionCategory => (ACTION_CATEGORIES as string[]).includes(c)) : [],
  };
}

function syncLogFiltersToUrl(filters: AuditLogFilters) {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const sp = new URLSearchParams(window.location.search);
  sp.set('section', 'audit');
  sp.set('auditView', 'users');
  const setOrDelete = (k: string, v: string) => { if (v) sp.set(k, v); else sp.delete(k); };
  setOrDelete('logDatePreset', filters.datePreset === '30d' ? '' : filters.datePreset);
  setOrDelete('logDateFrom', filters.datePreset === 'custom' ? filters.dateFrom : '');
  setOrDelete('logDateTo', filters.datePreset === 'custom' ? filters.dateTo : '');
  setOrDelete('logActorUserId', filters.actorUserId);
  setOrDelete('logActorLabel', filters.actorLabel);
  setOrDelete('logTargetUserId', filters.targetUserId);
  setOrDelete('logTargetLabel', filters.targetLabel);
  setOrDelete('actionPrefix', filters.categories.join(','));
  const qs = sp.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
}

/** Resolves a date preset to concrete ISO `dateFrom`/`dateTo` bounds
 *  sent to the API. 'custom' passes through the operator's own
 *  YYYY-MM-DD picks (day-boundary padded, same convention as the
 *  Resources sub-tab). Computed fresh on every render off `Date.now()`
 *  — cheap, and keeps "Last 24h" actually meaning the last 24h on a
 *  long-open tab rather than freezing at first render. */
function resolveDateBounds(filters: AuditLogFilters): { dateFrom?: string; dateTo?: string } {
  if (filters.datePreset === 'custom') {
    return {
      dateFrom: filters.dateFrom ? `${filters.dateFrom}T00:00:00.000Z` : undefined,
      dateTo: filters.dateTo ? `${filters.dateTo}T23:59:59.999Z` : undefined,
    };
  }
  const now = Date.now();
  const hours = filters.datePreset === '24h' ? 24 : filters.datePreset === '7d' ? 24 * 7 : 24 * 30;
  return { dateFrom: new Date(now - hours * 60 * 60 * 1000).toISOString(), dateTo: undefined };
}

function dateChipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    borderRadius: 4,
    border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
    background: active ? 'var(--accent-primary)' : 'var(--surface-raised)',
    color: active ? '#fff' : 'var(--text-primary)',
    cursor: 'pointer',
  };
}

function logActorCell(actor: AuditLogRow['actor'], actorId: AuditLogRow['actorId']) {
  if (actor) return <span title={actor.email ?? undefined}>{actor.name ?? actor.id}</span>;
  if (actorId) {
    return (
      <span
        title="Could not resolve a display name for this user id"
        style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}
      >
        {actorId}
      </span>
    );
  }
  return <em style={{ color: 'var(--text-muted)' }}>system</em>;
}

function UserOrgAuditLogPanel() {
  const [filters, setFilters] = useState<AuditLogFilters>(() => readLogFiltersFromUrl());
  const [actorQuery, setActorQuery] = useState('');
  const [targetQuery, setTargetQuery] = useState('');
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [rawRow, setRawRow] = useState<AuditLogRow | null>(null);

  useEffect(() => {
    syncLogFiltersToUrl(filters);
  }, [filters]);

  function updateFilters(patch: Partial<AuditLogFilters>) {
    setFilters((f) => ({ ...f, ...patch }));
    setCursors([null]);
    setPageIndex(0);
  }

  function toggleCategory(cat: AuditLogActionCategory) {
    updateFilters({
      categories: filters.categories.includes(cat)
        ? filters.categories.filter((c) => c !== cat)
        : [...filters.categories, cat],
    });
  }

  const cursor = cursors[pageIndex] ?? null;
  const { dateFrom, dateTo } = resolveDateBounds(filters);

  const q = useQuery({
    queryKey: ['admin-audit-log', filters.datePreset, filters.dateFrom, filters.dateTo, filters.actorUserId, filters.targetUserId, filters.categories.join(','), cursor],
    queryFn: () =>
      apiList<AuditLogRow>('/admin/audit-log', {
        query: {
          dateFrom,
          dateTo,
          actorUserId: filters.actorUserId || undefined,
          targetUserId: filters.targetUserId || undefined,
          actionPrefix: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
          cursor: cursor ?? undefined,
          limit: 25,
        },
      }),
  });

  const rows = q.data?.data ?? [];
  const nextCursor = q.data?.page.nextCursor ?? null;
  const hasNextPage = !!nextCursor;
  const hasPrevPage = pageIndex > 0;

  function goNext() {
    if (!nextCursor) return;
    setCursors((c) => { const next = c.slice(0, pageIndex + 1); next.push(nextCursor); return next; });
    setPageIndex((i) => i + 1);
  }
  function goPrev() {
    if (pageIndex === 0) return;
    setPageIndex((i) => i - 1);
  }

  const anyFilterActive = useMemo(
    () => filters.datePreset !== '30d' || !!filters.actorUserId || !!filters.targetUserId || filters.categories.length > 0,
    [filters],
  );

  if (q.isError) {
    const err = q.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          Viewing the user &amp; org audit log requires the <code>audit:view</code> capability.
        </div>
      );
    }
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load audit log rows.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Users &amp; orgs</h2>
        </div>
        <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          User, organization, group, and global-role mutations — account creation/disable, org
          membership + ownership changes, group membership, cross-org role grants. Scoped to this
          identity/access slice of the audit log, not the full history (see the Resources tab for
          document/tool/nest activity).
        </p>

        {/* ── Filter bar ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>Date range</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(['24h', '7d', '30d', 'custom'] as DatePreset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={filters.datePreset === p}
                  onClick={() => updateFilters({ datePreset: p })}
                  style={dateChipStyle(filters.datePreset === p)}
                >
                  {p === '24h' ? 'Last 24h' : p === '7d' ? 'Last 7d' : p === '30d' ? 'Last 30d' : 'Custom'}
                </button>
              ))}
            </div>
            {filters.datePreset === 'custom' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilters({ dateFrom: e.target.value })}
                  style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4 }}
                  aria-label="Custom date from"
                />
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilters({ dateTo: e.target.value })}
                  style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4 }}
                  aria-label="Custom date to"
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, minWidth: 200 }}>
            <span style={{ fontWeight: 500 }}>Actor</span>
            {filters.actorUserId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid var(--border-strong)', borderRadius: 4 }}>
                <span style={{ fontSize: 13, flex: 1 }}>{filters.actorLabel || filters.actorUserId}</span>
                <button
                  type="button"
                  onClick={() => { updateFilters({ actorUserId: '', actorLabel: '' }); setActorQuery(''); }}
                  aria-label="Clear actor filter"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  ×
                </button>
              </div>
            ) : (
              <UserTypeahead
                value={actorQuery}
                onChange={setActorQuery}
                onSelect={(u) => { updateFilters({ actorUserId: u.id, actorLabel: u.displayName }); setActorQuery(''); }}
                placeholder="Filter by actor…"
                ariaLabel="Filter audit log by actor"
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, minWidth: 200 }}>
            <span style={{ fontWeight: 500 }}>Target user</span>
            {filters.targetUserId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid var(--border-strong)', borderRadius: 4 }}>
                <span style={{ fontSize: 13, flex: 1 }}>{filters.targetLabel || filters.targetUserId}</span>
                <button
                  type="button"
                  onClick={() => { updateFilters({ targetUserId: '', targetLabel: '' }); setTargetQuery(''); }}
                  aria-label="Clear target user filter"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  ×
                </button>
              </div>
            ) : (
              <UserTypeahead
                value={targetQuery}
                onChange={setTargetQuery}
                onSelect={(u) => { updateFilters({ targetUserId: u.id, targetLabel: u.displayName }); setTargetQuery(''); }}
                placeholder="Filter by affected user…"
                ariaLabel="Filter audit log by target user"
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>Action type</span>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
              {ACTION_CATEGORIES.map((cat) => (
                <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <input type="checkbox" checked={filters.categories.includes(cat)} onChange={() => toggleCategory(cat)} />
                  {ACTION_CATEGORY_LABELS[cat]}
                </label>
              ))}
            </div>
          </div>

          {anyFilterActive && (
            <button
              type="button"
              className="btn"
              onClick={() => updateFilters({ datePreset: '30d', dateFrom: '', dateTo: '', actorUserId: '', actorLabel: '', targetUserId: '', targetLabel: '', categories: [] })}
              style={{ fontSize: 12, padding: '6px 10px', alignSelf: 'flex-end' }}
            >
              Clear filters
            </button>
          )}
        </div>

        {/* ── Table ──────────────────────────────────────────────── */}
        {q.isLoading ? (
          <SkeletonRows rows={8} />
        ) : rows.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
            No audit log rows match the current filters.
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-subtle)' }}>
                  <th style={{ padding: '6px 8px' }}>Timestamp</th>
                  <th style={{ padding: '6px 8px' }}>Actor</th>
                  <th style={{ padding: '6px 8px' }}>Action</th>
                  <th style={{ padding: '6px 8px' }}>Target</th>
                  <th style={{ padding: '6px 8px' }}>Diff summary</th>
                  <th style={{ padding: '6px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                      {formatTimestamp(r.timestamp)}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{logActorCell(r.actor, r.actorId)}</td>
                    <td style={{ padding: '6px 8px' }}><code style={{ fontSize: 12 }}>{r.action}</code></td>
                    <td style={{ padding: '6px 8px' }}>
                      {r.targetType}{' '}
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{r.targetId}</span>
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{r.diffSummary}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setRawRow(r)}
                        style={{ fontSize: 11, padding: '3px 8px' }}
                      >
                        Show raw
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button type="button" className="btn" disabled={!hasPrevPage} onClick={goPrev} style={{ fontSize: 12, padding: '4px 10px' }}>
                ← Newer
              </button>
              <button type="button" className="btn" disabled={!hasNextPage} onClick={goNext} style={{ fontSize: 12, padding: '4px 10px' }}>
                Older →
              </button>
            </div>
          </>
        )}
      </section>

      {/* ── "Show raw" modal ───────────────────────────────────────── */}
      {rawRow && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Audit log row detail"
          onClick={() => setRawRow(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface-raised)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)', padding: 20, maxWidth: 640, width: '90%',
              maxHeight: '80vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}><code>{rawRow.action}</code></h3>
              <button type="button" onClick={() => setRawRow(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
            <dl style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', marginBottom: 12 }}>
              <dt>Target</dt><dd style={{ fontFamily: 'var(--font-mono)' }}>{rawRow.targetType} / {rawRow.targetId}</dd>
              <dt>Time</dt><dd>{formatTimestamp(rawRow.timestamp)}</dd>
              <dt>Actor</dt><dd>{logActorCell(rawRow.actor, rawRow.actorId)}</dd>
              {rawRow.message && (<><dt>Message</dt><dd>{rawRow.message}</dd></>)}
            </dl>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>BEFORE</div>
                <pre style={{ fontSize: 11, background: 'var(--surface-sunken)', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
                  {JSON.stringify(rawRow.before ?? null, null, 2)}
                </pre>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>AFTER</div>
                <pre style={{ fontSize: 11, background: 'var(--surface-sunken)', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
                  {JSON.stringify(rawRow.after ?? null, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
